import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LatestReadings, mergeLatestReadings } from "./latestReadings";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { useLiveDataLookups } from "@/pages/live-data/hooks/useLiveDataLookups";
import { LiveDataRow } from "@/pages/live-data/models/LiveDataRow";
import { LiveDataLookups, toLiveDataRow } from "@/pages/live-data/models/toLiveDataRow";
import { chunkArray } from "@/utilities/chunkArray";
import { fetchStatusDataComplete } from "@/utilities/query/fetchStatusDataComplete";

/** Where a scan has got to, and what it covered. */
export interface FleetScan {
  isRunning: boolean;
  pagesLoaded: number;
  recordsProcessed: number;
  /** How many of the scanned vehicles have reported anything at all so far. */
  deviceCount: number;
  /** Which vehicles the scan covered; anything else has not been checked rather than failed. */
  scannedIds: Set<string>;
  /** The window it covered, frozen when it started, or null before one has run. */
  window: { from: Date; to: Date } | null;
  error: Error | null;
  /**
   * What has been read so far.
   *
   * The same map throughout a scan, filled in place — copying a fleet's readings per page is
   * exactly what this hook exists to avoid. It travels in the snapshot rather than in a ref so
   * that everything derived from it has an honest dependency: a new snapshot means new readings.
   */
  readings: LatestReadings;
}

/**
 * A fresh idle scan.
 *
 * A function rather than a shared constant: setting state to the same object makes React skip the
 * re-render, and this hook's readings live in a ref whose changes only become visible through one.
 */
function idleScan(): FleetScan {
  return {
    isRunning: false,
    pagesLoaded: 0,
    recordsProcessed: 0,
    deviceCount: 0,
    scannedIds: new Set(),
    window: null,
    error: null,
    readings: new Map(),
  };
}

/**
 * Records per request, per vehicle.
 *
 * Note that the client batches the concurrent requests of one round into a single
 * `ExecuteMultiCall`, so what actually crosses the wire is up to this times the batch size.
 */
const RECORDS_PER_PAGE = 2_500;

/**
 * Vehicles requested at a time.
 *
 * A task is one vehicle, and every task handed to `fetchStatusDataComplete` starts at once — so
 * without batching a fleet-sized scan opens tens of thousands of concurrent paging loops and the
 * tab stops responding long before the scan finishes. Batches run one after another.
 */
const DEVICES_PER_BATCH = 100;

/** How often the grid is refreshed while the scan runs. */
const REFRESH_INTERVAL_MS = 500;

const MS_PER_HOUR = 60 * 60 * 1000;

/** Before the diagnostics query has answered; rows then carry raw ids until it does. */
const NO_LOOKUPS: LiveDataLookups = { diagnostics: {}, units: {}, sources: {} };

/**
 * Scans what every vehicle reported within a time window.
 *
 * The scan is started explicitly rather than on mount: it walks the whole fleet's readings and is
 * far too expensive to run because someone opened the page.
 *
 * Only the vehicles a page actually touched are rebuilt on each refresh, and the rest keep their
 * previous arrays. A full rebuild is a pass over every vehicle and every diagnostic — at fleet
 * size that alone was enough to stop the tab responding.
 *
 * @param hoursBack - How far back to look.
 * @param deviceIds - The vehicles to scan. Nothing happens while this is empty.
 */
export function useFleetStatusFeed(hoursBack: number = 24, deviceIds: string[] = []) {
  const { geotabClient, database, username } = useGeotabClient();

  const abortRef = useRef<AbortController | null>(null);
  const rowsRef = useRef<Record<string, LiveDataRow[]>>({});
  const lookupsRef = useRef<LiveDataLookups>(NO_LOOKUPS);

  const [scan, setScan] = useState<FleetScan>(idleScan);

  const clear = useCallback(() => {
    abortRef.current?.abort();
    rowsRef.current = {};
    setScan(idleScan());
  }, []);

  const start = useCallback(() => {
    if (deviceIds.length === 0) {
      return;
    }

    clear();
    const controller = new AbortController();
    abortRef.current = controller;

    // Captured once, so the window does not slide while the scan runs and vehicles scanned last
    // are not judged against a different period than those scanned first. Reported back, because
    // the export has to name the window that was actually covered.
    const toDate = new Date();
    const fromDate = new Date(toDate.getTime() - hoursBack * MS_PER_HOUR);
    const scannedIds = new Set(deviceIds);

    let pagesLoaded = 0;
    let recordsProcessed = 0;
    let lastRefresh = 0;
    const dirty = new Set<string>();
    const readings: LatestReadings = new Map();

    const publish = () => {
      lastRefresh = Date.now();
      rowsRef.current = rebuildRows(rowsRef.current, readings, dirty, lookupsRef.current);
      dirty.clear();
      setScan(current => ({
        ...current,
        pagesLoaded,
        recordsProcessed,
        deviceCount: readings.size,
      }));
    };

    setScan({
      ...idleScan(),
      isRunning: true,
      scannedIds,
      window: { from: fromDate, to: toDate },
      readings,
    });

    const scanFleet = async () => {
      for (const batch of chunkArray(deviceIds, DEVICES_PER_BATCH)) {
        controller.signal.throwIfAborted();

        await fetchStatusDataComplete(geotabClient, {
          tasks: batch.map(deviceId => ({ key: deviceId, deviceId })),
          fromDate,
          toDate,
          resultsLimit: RECORDS_PER_PAGE,
          signal: controller.signal,
          onPage: (deviceId, page) => {
            recordsProcessed += mergeLatestReadings(readings, deviceId, page);
            pagesLoaded += 1;
            dirty.add(deviceId);

            if (Date.now() - lastRefresh >= REFRESH_INTERVAL_MS) {
              publish();
            }
          },
        });
      }
    };

    scanFleet()
      .then(() => {
        if (controller.signal.aborted) {
          return;
        }
        publish();
        setScan(current => ({ ...current, isRunning: false }));
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) {
          return;
        }
        publish();
        setScan(current => ({
          ...current,
          isRunning: false,
          error: error instanceof Error ? error : new Error(String(error)),
        }));
      });
  }, [clear, deviceIds, geotabClient, hoursBack]);

  const stop = useCallback(() => {
    abortRef.current?.abort();
    setScan(current => ({ ...current, isRunning: false }));
  }, []);

  useEffect(() => () => abortRef.current?.abort(), []);

  // Thrown away when the window changes, because the results answered a different question, and
  // when the login changes, because device ids do not carry across databases. Keyed on the
  // credentials rather than on activeSessionId, which stays null for the whole life of the add-in
  // when it runs embedded in MyGeotab.
  const sessionKey = `${username}@${database}`;
  useEffect(() => {
    clear();
  }, [clear, sessionKey, hoursBack]);

  const diagnosticIds = useMemo(() => collectDiagnosticIds(scan.readings), [scan]);

  const { lookups } = useLiveDataLookups(diagnosticIds);

  // The names and units arrive from their own query, later than the readings. When they land,
  // every row has to be built again -- until then the rows carry raw ids.
  const rowsByDeviceId = useMemo(() => {
    if (lookupsRef.current !== lookups) {
      lookupsRef.current = lookups;
      rowsRef.current = rebuildRows({}, scan.readings, scan.readings.keys(), lookups);
    }
    return rowsRef.current;
  }, [scan, lookups]);

  return { rowsByDeviceId, scan, start, stop };
}

/**
 * Rebuilds the rows of the vehicles named in `dirty`, keeping the rest as they were.
 *
 * Keeping the untouched arrays is what makes the refresh cheap: their identity survives, so the
 * page's own per-vehicle memos and the grid see them as unchanged.
 */
function rebuildRows(
  previous: Record<string, LiveDataRow[]>,
  readings: LatestReadings,
  dirty: Iterable<string>,
  lookups: LiveDataLookups,
): Record<string, LiveDataRow[]> {
  const rows = { ...previous };

  for (const deviceId of dirty) {
    const byDiagnostic = readings.get(deviceId);
    rows[deviceId] = [...(byDiagnostic?.values() ?? [])].map(reading =>
      toLiveDataRow(reading, lookups),
    );
  }

  return rows;
}

function collectDiagnosticIds(readings: LatestReadings): string[] {
  const ids = new Set<string>();

  for (const byDiagnostic of readings.values()) {
    for (const diagnosticId of byDiagnostic.keys()) {
      ids.add(diagnosticId);
    }
  }

  // Sorted, because this array is part of the diagnostics query key: in discovery order it changes
  // with every page that brings a new diagnostic, refetching all of them again.
  return [...ids].sort();
}
